// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { AvailableServiceAliases } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type {
  AvailableServiceAlias,
  AvailableServiceAliasesListNextOptionalParams,
  AvailableServiceAliasesListOptionalParams,
  AvailableServiceAliasesListResponse,
  AvailableServiceAliasesListByResourceGroupNextOptionalParams,
  AvailableServiceAliasesListByResourceGroupOptionalParams,
  AvailableServiceAliasesListByResourceGroupResponse,
  AvailableServiceAliasesListNextResponse,
  AvailableServiceAliasesListByResourceGroupNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing AvailableServiceAliases operations. */
export class AvailableServiceAliasesImpl implements AvailableServiceAliases {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class AvailableServiceAliases class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets all available service aliases for this subscription in this region.
   * @param location The location.
   * @param options The options parameters.
   */
  public list(
    location: string,
    options?: AvailableServiceAliasesListOptionalParams,
  ): PagedAsyncIterableIterator<AvailableServiceAlias> {
    const iter = this.listPagingAll(location, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(location, options, settings);
      },
    };
  }

  private async *listPagingPage(
    location: string,
    options?: AvailableServiceAliasesListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<AvailableServiceAlias[]> {
    let result: AvailableServiceAliasesListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(location, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(location, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    location: string,
    options?: AvailableServiceAliasesListOptionalParams,
  ): AsyncIterableIterator<AvailableServiceAlias> {
    for await (const page of this.listPagingPage(location, options)) {
      yield* page;
    }
  }

  /**
   * Gets all available service aliases for this resource group in this region.
   * @param resourceGroupName The name of the resource group.
   * @param location The location.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    location: string,
    options?: AvailableServiceAliasesListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<AvailableServiceAlias> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, location, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByResourceGroupPagingPage(resourceGroupName, location, options, settings);
      },
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    location: string,
    options?: AvailableServiceAliasesListByResourceGroupOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<AvailableServiceAlias[]> {
    let result: AvailableServiceAliasesListByResourceGroupResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByResourceGroup(resourceGroupName, location, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByResourceGroupNext(
        resourceGroupName,
        location,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    location: string,
    options?: AvailableServiceAliasesListByResourceGroupOptionalParams,
  ): AsyncIterableIterator<AvailableServiceAlias> {
    for await (const page of this.listByResourceGroupPagingPage(
      resourceGroupName,
      location,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets all available service aliases for this subscription in this region.
   * @param location The location.
   * @param options The options parameters.
   */
  private _list(
    location: string,
    options?: AvailableServiceAliasesListOptionalParams,
  ): Promise<AvailableServiceAliasesListResponse> {
    return this.client.sendOperationRequest({ location, options }, listOperationSpec);
  }

  /**
   * Gets all available service aliases for this resource group in this region.
   * @param resourceGroupName The name of the resource group.
   * @param location The location.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    location: string,
    options?: AvailableServiceAliasesListByResourceGroupOptionalParams,
  ): Promise<AvailableServiceAliasesListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, location, options },
      listByResourceGroupOperationSpec,
    );
  }

  /**
   * ListNext
   * @param location The location.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    location: string,
    nextLink: string,
    options?: AvailableServiceAliasesListNextOptionalParams,
  ): Promise<AvailableServiceAliasesListNextResponse> {
    return this.client.sendOperationRequest({ location, nextLink, options }, listNextOperationSpec);
  }

  /**
   * ListByResourceGroupNext
   * @param resourceGroupName The name of the resource group.
   * @param location The location.
   * @param nextLink The nextLink from the previous successful call to the ListByResourceGroup method.
   * @param options The options parameters.
   */
  private _listByResourceGroupNext(
    resourceGroupName: string,
    location: string,
    nextLink: string,
    options?: AvailableServiceAliasesListByResourceGroupNextOptionalParams,
  ): Promise<AvailableServiceAliasesListByResourceGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, location, nextLink, options },
      listByResourceGroupNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/locations/{location}/availableServiceAliases",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailableServiceAliasesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.location],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/locations/{location}/availableServiceAliases",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailableServiceAliasesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.location,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailableServiceAliasesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.location,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByResourceGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailableServiceAliasesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.location,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
